import {
  getFirestore,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  onSnapshot,
  serverTimestamp,
  increment,
  writeBatch,
  runTransaction,
} from 'firebase/firestore';
import {
  FUNDS_EARN_CENTS,
  FUNDS_LEDGER_TYPES,
  MUTUAL_FUNDS_COLLECTION,
  USER_FUNDS_HISTORY_COLLECTION,
  MAIN_MUTUAL_FUND_ID,
  MAIN_MUTUAL_FUND_DEFAULTS,
  clampBillingReserveCents,
  WALLET_MAX_PEER_TRANSFER_CENTS,
  WALLET_PEER_SENDS_COLLECTION,
  WALLET_PEER_SEND_ONCE_MESSAGE,
} from '../constants/userFunds';

function userRef(uid) {
  return doc(getFirestore(), 'users', uid);
}

function historyCol(uid) {
  return collection(getFirestore(), 'users', uid, USER_FUNDS_HISTORY_COLLECTION);
}

function payoutHistoryCol(uid) {
  return collection(getFirestore(), 'users', uid, 'payout_history');
}

function peerSendLockRef(fromUid, toUid) {
  return doc(
    getFirestore(),
    'users',
    fromUid,
    WALLET_PEER_SENDS_COLLECTION,
    toUid,
  );
}

/** True if this sender already completed a Wallet peer send to the recipient. */
export async function hasPeerWalletSendToRecipient(fromUid, toUid) {
  if (!fromUid || !toUid || fromUid === toUid) return false;
  const lockSnap = await getDoc(peerSendLockRef(fromUid, toUid));
  if (lockSnap.exists()) return true;
  const snap = await getDocs(query(payoutHistoryCol(fromUid), limit(200)));
  return snap.docs.some((d) => {
    const row = d.data() || {};
    return row.type === 'transfer_out' && row.counterpartyUid === toUid;
  });
}

export function subscribeUserFunds(uid, onData, onError) {
  if (!uid) {
    onData?.({
      balanceCents: 0,
      reservedForBillingCents: 0,
      payoutBalanceCents: 0,
      payoutWithdrawnCents: 0,
    });
    return () => {};
  }
  return onSnapshot(
    userRef(uid),
    (snap) => {
      const data = snap.exists() ? snap.data() : {};
      onData?.({
        balanceCents: Math.max(0, Number(data.fundsBalanceCents) || 0),
        reservedForBillingCents: Math.max(0, Number(data.fundsReservedForBillingCents) || 0),
        payoutBalanceCents: Math.max(0, Number(data.payoutBalanceCents) || 0),
        payoutBalanceReferralHoldCents: Math.max(0, Number(data.payoutBalanceReferralHoldCents) || 0),
        payoutWithdrawnCents: Math.max(0, Number(data.payoutWithdrawnCents) || 0),
      });
    },
    onError,
  );
}

export function subscribeFundsHistory(uid, onData, onError, max = 40) {
  if (!uid) {
    onData?.([]);
    return () => {};
  }
  const q = query(historyCol(uid), orderBy('createdAt', 'desc'), limit(max));
  return onSnapshot(
    q,
    (snap) => {
      onData?.(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    },
    onError,
  );
}

export function subscribeActiveMutualFunds(onData, onError) {
  const db = getFirestore();
  const col = collection(db, MUTUAL_FUNDS_COLLECTION);

  let activeUnsub = null;
  const attachLoose = () => {
    activeUnsub = onSnapshot(
      query(col, where('active', '==', true)),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => {
          if (a.isMain && !b.isMain) return -1;
          if (!a.isMain && b.isMain) return 1;
          if (a.id === MAIN_MUTUAL_FUND_ID && b.id !== MAIN_MUTUAL_FUND_ID) return -1;
          if (a.id !== MAIN_MUTUAL_FUND_ID && b.id === MAIN_MUTUAL_FUND_ID) return 1;
          return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
        });
        onData?.(rows);
      },
      onError,
    );
  };

  activeUnsub = onSnapshot(
    query(col, where('active', '==', true), orderBy('sortOrder', 'asc')),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        if (a.isMain && !b.isMain) return -1;
        if (!a.isMain && b.isMain) return 1;
        if (a.id === MAIN_MUTUAL_FUND_ID && b.id !== MAIN_MUTUAL_FUND_ID) return -1;
        if (a.id !== MAIN_MUTUAL_FUND_ID && b.id === MAIN_MUTUAL_FUND_ID) return 1;
        return (Number(a.sortOrder) || 0) - (Number(b.sortOrder) || 0);
      });
      onData?.(rows);
    },
    (err) => {
      if (err?.code === 'failed-precondition' || err?.code === 'invalid-argument') {
        try { activeUnsub?.(); } catch (_) {}
        attachLoose();
        return;
      }
      onError?.(err);
    },
  );

  return () => {
    try { activeUnsub?.(); } catch (_) {}
  };
}

/**
 * Credit contribution Credits when an admin approves a submission ($1 by default).
 * Credits do NOT enter Wallet (payoutBalanceCents). Idempotent via sourceKey.
 */
export async function creditUserFundEarn({
  uid,
  amountCents = FUNDS_EARN_CENTS,
  label,
  sourceCollection,
  sourceId,
  meta = {},
}) {
  if (!uid || !sourceCollection || !sourceId) return { credited: false, reason: 'missing_fields' };
  // Credits may be fractional (admin credit economy allows 2 decimals).
  const amount = Math.max(0, Math.round((Number(amountCents) || 0) * 100) / 100);
  if (!amount) return { credited: false, reason: 'zero_amount' };

  const sourceKey = `${sourceCollection}:${sourceId}`;
  const existing = await getDocs(
    query(historyCol(uid), where('sourceKey', '==', sourceKey), limit(1)),
  );
  if (!existing.empty) return { credited: false, reason: 'already_credited' };

  const batch = writeBatch(getFirestore());
  batch.set(userRef(uid), {
    fundsBalanceCents: increment(amount),
    fundsUpdatedAt: serverTimestamp(),
  }, { merge: true });
  const entryRef = doc(historyCol(uid));
  batch.set(entryRef, {
    type: FUNDS_LEDGER_TYPES.EARN,
    amountCents: amount,
    label: label || 'Approved contribution (+1 Credit)',
    sourceKey,
    sourceCollection,
    sourceId,
    meta,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
  return { credited: true, amountCents: amount, entryId: entryRef.id };
}

/** Legacy: reserve field unused — Credits auto-apply on invoices. */
export async function setBillingReserve({ uid, amountCents }) {
  if (!uid) throw new Error('Sign in required');
  return runTransaction(getFirestore(), async (tx) => {
    const snap = await tx.get(userRef(uid));
    if (!snap.exists()) throw new Error('User not found');
    const data = snap.data() || {};
    const balance = Math.max(0, Number(data.fundsBalanceCents) || 0);
    const prevReserved = Math.max(0, Number(data.fundsReservedForBillingCents) || 0);
    const nextReserved = clampBillingReserveCents(amountCents, balance + prevReserved);

    // Moving reserve ↔ available: balance is spendable pool excluding reserved.
    // Model: fundsBalanceCents = total unspent wallet; reserved is a carve-out of that total.
    // So available = balance - reserved. When changing reserve, only update reserved field
    // if reserved ≤ balance.
    const capped = Math.min(nextReserved, balance);
    const delta = capped - prevReserved;

    tx.update(userRef(uid), {
      fundsReservedForBillingCents: capped,
      fundsUpdatedAt: serverTimestamp(),
    });

    if (delta !== 0) {
      const entryRef = doc(historyCol(uid));
      tx.set(entryRef, {
        type: delta > 0 ? FUNDS_LEDGER_TYPES.BILLING_RESERVE : FUNDS_LEDGER_TYPES.BILLING_RELEASE,
        amountCents: Math.abs(delta),
        label: delta > 0
          ? `Reserved ${formatLabel(Math.abs(delta))} for next bill`
          : `Released ${formatLabel(Math.abs(delta))} from billing reserve`,
        createdAt: serverTimestamp(),
      });
    }

    return { reservedForBillingCents: capped, balanceCents: balance };
  });
}

function formatLabel(cents) {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;
}

/** Ensure the canonical MAIN mutual fund doc exists (cause voted later). */
export async function ensureMainMutualFund() {
  const ref = doc(getFirestore(), MUTUAL_FUNDS_COLLECTION, MAIN_MUTUAL_FUND_ID);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      ...MAIN_MUTUAL_FUND_DEFAULTS,
      raisedCents: 0,
      donorCount: 0,
      coverImageUrl: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return { id: MAIN_MUTUAL_FUND_ID, created: true };
  }
  const data = snap.data() || {};
  const patch = {};
  if (!data.isMain) patch.isMain = true;
  if (data.causePending === undefined) patch.causePending = true;
  if (data.active === false) patch.active = true;
  if (Number(data.sortOrder) > -1000) patch.sortOrder = -1000;
  if (!String(data.title || '').trim()) patch.title = MAIN_MUTUAL_FUND_DEFAULTS.title;
  if (Object.keys(patch).length) {
    patch.updatedAt = serverTimestamp();
    await updateDoc(ref, patch);
  }
  return { id: MAIN_MUTUAL_FUND_ID, created: false };
}

/** Donate from Wallet (payoutBalanceCents) into a mutual fund. Credits cannot donate. */
export async function donateToMutualFund({ uid, fundId, amountCents }) {
  if (!uid || !fundId) throw new Error('Missing donation target');
  const amount = Math.max(0, Math.round(Number(amountCents) || 0));
  if (!amount) throw new Error('Enter an amount to donate');

  return runTransaction(getFirestore(), async (tx) => {
    const uSnap = await tx.get(userRef(uid));
    const fRef = doc(getFirestore(), MUTUAL_FUNDS_COLLECTION, fundId);
    const fSnap = await tx.get(fRef);
    if (!uSnap.exists()) throw new Error('User not found');
    if (!fSnap.exists()) throw new Error('Mutual fund not found');
    const fund = fSnap.data() || {};
    if (!fund.active) throw new Error('This mutual fund is not active');

    const payoutBalance = Math.max(0, Number(uSnap.data().payoutBalanceCents) || 0);
    if (amount > payoutBalance) throw new Error('Not enough Wallet balance');

    tx.update(userRef(uid), {
      payoutBalanceCents: increment(-amount),
      payoutUpdatedAt: serverTimestamp(),
    });
    tx.update(fRef, {
      raisedCents: increment(amount),
      donorCount: increment(1),
      updatedAt: serverTimestamp(),
    });
    const payoutHistRef = doc(collection(getFirestore(), 'users', uid, 'payout_history'));
    tx.set(payoutHistRef, {
      type: 'donate_out',
      amountCents: amount,
      label: `Donated ${formatLabel(amount)} to ${fund.title || 'Mutual Fund'}`,
      mutualFundId: fundId,
      createdAt: serverTimestamp(),
    });

    return { donatedCents: amount };
  });
}

/** Look up a VRC user by email (exact, case-insensitive). */
export async function findUserByEmailForTransfer(email) {
  const emailLower = String(email || '').trim().toLowerCase();
  if (!emailLower || !emailLower.includes('@')) return null;
  const snap = await getDocs(
    query(collection(getFirestore(), 'users'), where('email', '==', emailLower), limit(1)),
  );
  if (snap.empty) return null;
  const d = snap.docs[0];
  const data = d.data() || {};
  return {
    uid: d.id,
    email: data.email || emailLower,
    displayName: data.displayName || data.username || data.name || data.email || 'VRC user',
  };
}

/** Transfer Wallet funds to another VRC user. Credits cannot be transferred. */
export async function transferFundsToUser({ fromUid, toUid, amountCents, toLabel = '' }) {
  if (!fromUid || !toUid) throw new Error('Missing transfer parties');
  if (fromUid === toUid) throw new Error('You cannot send funds to yourself');
  const amount = Math.max(0, Math.round(Number(amountCents) || 0));
  if (!amount) throw new Error('Enter an amount to send');
  if (amount > WALLET_MAX_PEER_TRANSFER_CENTS) {
    throw new Error(
      `Member sends are limited to ${formatLabel(WALLET_MAX_PEER_TRANSFER_CENTS)} per transfer. `
      + 'Use a tour fund disbursement for larger amounts.',
    );
  }

  if (await hasPeerWalletSendToRecipient(fromUid, toUid)) {
    throw new Error(WALLET_PEER_SEND_ONCE_MESSAGE);
  }

  return runTransaction(getFirestore(), async (tx) => {
    const lockRef = peerSendLockRef(fromUid, toUid);
    const lockSnap = await tx.get(lockRef);
    if (lockSnap.exists()) {
      throw new Error(WALLET_PEER_SEND_ONCE_MESSAGE);
    }

    const fromSnap = await tx.get(userRef(fromUid));
    const toSnap = await tx.get(userRef(toUid));
    if (!fromSnap.exists()) throw new Error('Your account was not found');
    if (!toSnap.exists()) throw new Error('Recipient account was not found');

    const fromData = fromSnap.data() || {};
    const payoutBalance = Math.max(0, Number(fromData.payoutBalanceCents) || 0);
    if (amount > payoutBalance) throw new Error('Not enough Wallet balance');

    const toName = toLabel
      || toSnap.data()?.displayName
      || toSnap.data()?.username
      || toSnap.data()?.email
      || 'VRC user';
    const fromName = fromData.displayName || fromData.username || fromData.email || 'VRC user';

    tx.set(userRef(fromUid), {
      payoutBalanceCents: increment(-amount),
      payoutUpdatedAt: serverTimestamp(),
    }, { merge: true });
    tx.set(userRef(toUid), {
      payoutBalanceCents: increment(amount),
      payoutUpdatedAt: serverTimestamp(),
    }, { merge: true });

    const payoutOutRef = doc(collection(getFirestore(), 'users', fromUid, 'payout_history'));
    tx.set(payoutOutRef, {
      type: 'transfer_out',
      amountCents: amount,
      label: `Sent ${formatLabel(amount)} to ${toName}`,
      counterpartyUid: toUid,
      createdAt: serverTimestamp(),
    });
    const payoutInRef = doc(collection(getFirestore(), 'users', toUid, 'payout_history'));
    tx.set(payoutInRef, {
      type: 'transfer_in',
      amountCents: amount,
      label: `Received ${formatLabel(amount)} from ${fromName}`,
      counterpartyUid: fromUid,
      createdAt: serverTimestamp(),
    });

    tx.set(lockRef, {
      toUid,
      amountCents: amount,
      createdAt: serverTimestamp(),
    });

    return { transferredCents: amount, toUid };
  });
}

/** Admin: list all mutual funds (active + inactive). */
export async function listAllMutualFunds() {
  const snap = await getDocs(collection(getFirestore(), MUTUAL_FUNDS_COLLECTION));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
  return rows;
}

export async function upsertMutualFund(fund) {
  const payload = {
    title: String(fund.title || '').trim(),
    description: String(fund.description || '').trim(),
    goalCents: Math.max(0, Math.round(Number(fund.goalCents) || 0)),
    raisedCents: Math.max(0, Math.round(Number(fund.raisedCents) || 0)),
    active: Boolean(fund.active),
    sortOrder: Number(fund.sortOrder) || 0,
    updatedAt: serverTimestamp(),
  };
  if (fund.coverImageUrl !== undefined) {
    payload.coverImageUrl = fund.coverImageUrl
      ? String(fund.coverImageUrl).trim()
      : null;
  }
  if (!payload.title) throw new Error('Title is required');
  if (fund.id) {
    await updateDoc(doc(getFirestore(), MUTUAL_FUNDS_COLLECTION, fund.id), payload);
    return fund.id;
  }
  const ref = await addDoc(collection(getFirestore(), MUTUAL_FUNDS_COLLECTION), {
    ...payload,
    coverImageUrl: payload.coverImageUrl || null,
    raisedCents: payload.raisedCents || 0,
    donorCount: 0,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

export async function setMutualFundActive(fundId, active) {
  await updateDoc(doc(getFirestore(), MUTUAL_FUNDS_COLLECTION, fundId), {
    active: Boolean(active),
    updatedAt: serverTimestamp(),
  });
}

/** After Stripe applies reserved discount, clear reserve and deduct from balance. */
export async function consumeBillingReserve({ uid, appliedCents }) {
  if (!uid) return;
  const applied = Math.max(0, Math.round(Number(appliedCents) || 0));
  if (!applied) return;
  const snap = await getDoc(userRef(uid));
  if (!snap.exists()) return;
  const data = snap.data() || {};
  const balance = Math.max(0, Number(data.fundsBalanceCents) || 0);
  const reserved = Math.max(0, Number(data.fundsReservedForBillingCents) || 0);
  const use = Math.min(applied, reserved, balance);
  if (!use) return;

  const batch = writeBatch(getFirestore());
  batch.update(userRef(uid), {
    fundsBalanceCents: increment(-use),
    fundsReservedForBillingCents: Math.max(0, reserved - use),
    fundsUpdatedAt: serverTimestamp(),
  });
  const entryRef = doc(historyCol(uid));
  batch.set(entryRef, {
    type: FUNDS_LEDGER_TYPES.BILLING_APPLIED,
    amountCents: use,
    label: `Applied ${formatLabel(use)} to subscription invoice`,
    createdAt: serverTimestamp(),
  });
  await batch.commit();
}
